<template>
    <div v-if="isLoading" class="mb-5">
        <span class="fa fa-spinner fa-spin"></span>
    </div>
    <div v-else class="mb-5">
        <div v-if="error" class="alert alert-danger mt-4">{{ error }}</div>

        <div v-if="!hasHoldModels" class="alert alert-info">
            Niets gevonden
        </div>

        <!-- TO REVIEW -->
        <div v-for="holdReasonStatus in Object.keys(holdReasonsPerModelType).sort()" :key="holdReasonStatus">
            <h4 v-if="holdReasonStatus === 'current'" class="mt-4">
                Nog te reviewen
                <small>
                    <span class="fa fa-info-circle text-info ml-1"
                          title="Het kan voorkomen dat volledige gereviewede blokkeringen nog onder dit kopje staan omdat deze nog niet verwerkt zijn door het systeem. Binnen enkele minuten zou dit aangepast moeten zijn na een refresh van de pagina."></span>
                </small>
            </h4>
            <h4 v-else class="mt-4">Gereviewed</h4>

            <div v-for="holdReasonModelType in Object.keys(holdReasonsPerModelType[holdReasonStatus]).reverse()" v-if="holdReasonsPerModelType[holdReasonStatus][holdReasonModelType].length"
                 :key="`${holdReasonStatus}_${holdReasonModelType}`">
                <div v-if="!holdReasonsPerModelType[holdReasonStatus][holdReasonModelType].length && !error" class="alert alert-light mt-4">Geen</div>

                <b-card class="mt-4 mb-2" no-body v-for="holdReasonsForModel in groupHoldReasonsByModelId(holdReasonsPerModelType[holdReasonStatus][holdReasonModelType])"
                        :key="holdReasonsForModel[0].id">
                    <b-card-header class="d-block he-auto p-4">
                        <hold-reason-model-info-header :sales-order="salesOrder" :hold-reasons-for-model="holdReasonsForModel" :hold-model-type="holdReasonModelType"/>
                    </b-card-header>

                    <div class="card-body pb-4">
                        <div class="card-text">
                            <div class="row">
                                <div class="col-4 col-md"><strong>Reden</strong></div>
                                <div class="col-4 col-md"><strong>Commentaar</strong></div>
                                <div class="col-4 col-md-2 pr-0"><strong>Beslissing</strong></div>
                            </div>

                            <form v-for="reason in mapHoldReasons(holdReasonsForModel)" :key="reason.id" @submit.prevent="updateReason(reason)" class="hold-reasons-list">
                                <div class="row mt-4" :data-hold-reason-id="reason.id" :data-hold-rule-group-id="reason.hold_rule_group_id">
                                    <div class="col-4 col-md">{{ reason.hold_rule_group ? reason.hold_rule_group.display_name : 'Onbekend (niet opgeslagen)' }}</div>
                                    <div class="col-4 col-md">
                                        <textarea v-if="!reason.decision && hasPermission('orders.holdreview.edit')" required class="form-control form-control-sm" v-model.trim="reason.comment"
                                                  :disabled="isUpdatingReason"></textarea>
                                        <span v-else>{{ reason.comment }}</span>
                                    </div>

                                    <div v-if="!reason.decision" class="col-4 col-md-2 pr-0 btn-group-sm">
                                        <button v-if="isReasonApprovable(reason)"
                                                type="button"
                                                class="btn border-0 btn-outline-success"
                                                :title="thumbsUpTitle"
                                                @click="submitReasonForm(reason, consts.DECISION_STATUS_APPROVED)"
                                                :disabled="thumbsUpDisabled(reason)"
                                        >
                                            <span class="fa fa-thumbs-up"></span>
                                        </button>
                                        <button v-else
                                                type="button"
                                                class="btn border-0 btn-outline-secondary"
                                                title="Deze reden kan niet vrijgegeven worden. In plaats daarvan kan je de data van de order wijzigen. Hierna wordt de order opnieuw beoordeeld."
                                                :disabled="true"
                                        >
                                            <span class="fa fa-thumbs-up"></span>
                                        </button>
                                        <button type="button"
                                                class="btn btn-outline-danger border-0"
                                                :title="thumbsDownTitle"
                                                @click="submitReasonForm(reason, consts.DECISION_STATUS_DENIED)"
                                                :disabled="thumbsDisabled">
                                            <span class="fa fa-thumbs-down"></span>
                                        </button>
                                        <!-- This button is needed to submit the form and trigger HTML5 validation -->
                                        <button v-show="false" type="submit" :ref="`form_${reason.id}`"></button>
                                    </div>
                                    <div v-else class="col-4 col-md-2 pr-0 btn-group-sm">
                                        <span v-if="reason.decision === consts.DECISION_STATUS_APPROVED"
                                              class="lead text-success"
                                              :title="`Deze reden is vrijgegeven op ${formatDateTime(reason.decision_updated_at)}`">
                                            <span class="fa fa-thumbs-up"></span>
                                        </span>
                                        <span v-else-if="reason.decision === consts.DECISION_STATUS_DENIED"
                                              class="lead text-danger"
                                              v-b-tooltip="`Door deze reden is de blokkering definitief gemaakt op ${formatDateTime(reason.decision_updated_at)}`">
                                            <span class="fa fa-thumbs-down"></span>
                                        </span>
                                        <span v-else-if="reason.decision === consts.DECISION_STATUS_UNREVIEWABLE"
                                              class="text-secondary"
                                              v-b-tooltip="`Deze reden is niet beoordeeld omdat een andere reden al definitief is geblokkeerd`">
                                            <span class="icon-dash">-</span>
                                        </span>
                                        <span v-else-if="reason.decision === consts.DECISION_STATUS_INVALIDATED_BY_DATA_CHANGE"
                                              class="text-secondary"
                                              v-b-tooltip="`Deze reden hoeft niet meer te worden beoordeeld omdat de data van de order is gewijzigd. De order wordt opnieuw gecontroleerd a.d.h.v. de nieuwe data.`">
                                            <font-awesome-icon icon="ban" class="text-secondary"/>
                                        </span>
                                    </div>
                                </div>
                            </form>
                            <div v-if="!holdReasonsForModel.length" class="alert alert-danger mt-4">
                                Deze blokkering heeft geen redenen om te beoordelen.
                                Neem contact op met <a href="https://www.omnistock.nl/" target="_blank">OmniStock</a>.
                            </div>
                        </div>
                    </div>
                </b-card>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash"
import {hasPermission} from '../../helpers/permissions'
import HoldReasonModelInfoHeader from "@/views/OrderDetail/components/HoldReasonModelInfoHeader.vue";

export const DECISION_STATUS_APPROVED = 'on-hold-released-manual'
export const DECISION_STATUS_DENIED = 'on-hold-final-manual'
export const DECISION_STATUS_UNREVIEWABLE = 'not-reviewable-because-one-reason-is-made-final'
export const DECISION_STATUS_INVALIDATED_BY_DATA_CHANGE = 'on-hold-invalidated-by-data-change'

export default {
    name: "HoldReasonsReview",
    components: {HoldReasonModelInfoHeader},

    props: {
        salesOrder: {required: true}
    },

    data() {
        return {
            isLoading: true,
            isUpdatingReason: false,
            holdReasonsPerModelType: {},
            error: '',
            consts: {
                DECISION_STATUS_APPROVED: DECISION_STATUS_APPROVED,
                DECISION_STATUS_DENIED: DECISION_STATUS_DENIED,
                DECISION_STATUS_UNREVIEWABLE: DECISION_STATUS_UNREVIEWABLE,
                DECISION_STATUS_INVALIDATED_BY_DATA_CHANGE: DECISION_STATUS_INVALIDATED_BY_DATA_CHANGE,
            }
        }
    },

    computed: {
        salesOrderProductsByIdentifier() {
            return _.chain(this.salesOrder.sales_order_lines)
                .keyBy('product_identifier')
                .mapValues(line => line.product_name)
                .value()
        },
        hasHoldModels() {
            for (let model in this.holdReasonsPerModelType) {
                for (let group in this.holdReasonsPerModelType[model]) {
                    if (this.holdReasonsPerModelType[model][group].length) {
                        return true
                    }
                }
            }

            return false
        },

        thumbsDisabled() {
            if (!hasPermission('orders.holdreview.edit')) {
                return true
            }

            return this.isUpdatingReason;
        },

        thumbsUpTitle() {
            return !hasPermission('orders.holdreview.edit') ? 'Je hebt geen rechten om deze reden vrij te geven' : 'Reden vrijgeven'
        },

        thumbsDownTitle() {
            // Als je een reden afkeurt, wordt het gehele leveringsverzoek geblokkeerd.
            return !hasPermission('orders.holdreview.edit') ? 'Je hebt geen rechten om definitief te blokkeren' : 'Definitief blokkeren'
        },
    },

    mounted() {
        this.initialize()
    },

    methods: {
        initialize() {
            this.$http.all([
                this.$http.get(`/api/rest/sales-orders/${this.salesOrder.identifier}/hold-reasons/on-hold`),
            ])
                .then(this.$http.spread((holdReasons) => {
                    this.holdReasonsPerModelType = _.chain(holdReasons.data)
                        .groupBy(reason => {
                            switch (reason.holdable_type) {
                                case "App\\Models\\Omt\\DispatchRequest":
                                    return reason.holdable.is_replaced ? 'history' : 'current'
                                case "App\\Models\\Omt\\SalesOrder":
                                    return reason.decision_updated_at ? 'history' : 'current'
                                default:
                                    return 'history'
                            }
                        })
                        .mapValues(holdReasons => {
                            return _.groupBy(holdReasons, 'holdable_type')
                        })
                        .value()

                }))
                .catch(() => {
                    this.error = `Fout bij het laden van blokkeringen`
                    this.holdReasonsPerModelType = {
                        dispatch_request: {
                            current: [],
                            history: [],
                        },
                        sales_order: {
                            current: [],
                            history: [],
                        },
                    }
                })
                .then(() => {
                    this.isLoading = false
                })
        },
        updateReason(reason) {
            let confirmMessage = (reason.decision === DECISION_STATUS_APPROVED)
                ? `Weet je zeker dat je de blokkeerreden "${reason.hold_rule_group.display_name}" wilt vrijgeven?`
                : `Weet je zeker dat dit definitief wilt blokkeren?`

            if (!confirm(confirmMessage)) {
                reason.decision = null
                return false
            }

            this.isUpdatingReason = true

            this.$http.post(`/api/rest/sales-orders/${this.salesOrder.identifier}/hold-reasons/on-hold/${reason.id}`, {
                comment: (reason.comment) ? reason.comment : '',
                decision: reason.decision,
            })
                .then(() => {

                })
                .catch(() => {
                    reason.decision = null
                    this.error = `Fout bij het verwerken van de status`
                })
                .then(() => {
                    this.isUpdatingReason = false
                })
        },
        isDecisionStatusIconInvisible(decisionStatus, isIconApproved) {
            if (decisionStatus === DECISION_STATUS_UNREVIEWABLE) return true
            if (decisionStatus === DECISION_STATUS_DENIED && isIconApproved) return true
            if (decisionStatus === DECISION_STATUS_APPROVED && !isIconApproved) return true
            return false
        },
        submitReasonForm(reason, decision) {
            if (!reason.comment || !reason.comment.trim().length) {
                // Submit the form anyway to trigger the HTML5 form validation
                this.$refs[`form_${reason.id}`][0].click()
                return
            }

            reason.decision = decision
            this.$refs[`form_${reason.id}`][0].click()
        },
        groupHoldReasonsByModelId(holdReasons) {
            return _.chain(holdReasons)
                .groupBy('holdable_id')
                .value()
        },
        mapHoldReasons(holdReasons) {
            let hasOneItemDenied = holdReasons?.filter(item => item.decision === DECISION_STATUS_DENIED).length
            return holdReasons.map(item => {
                item.decision = (hasOneItemDenied && (!item.decision || !item.decision.length)) || item?.hold_rule_group?.is_status_hold_final
                    ? DECISION_STATUS_UNREVIEWABLE
                    : item.decision
                return item
            })
        },
        formatDateTime(datetime) {
            return new Date(datetime).toLocaleString()
        },
        isReasonApprovable(reason) {
            return reason.holdable_type !== "App\\Models\\Omt\\SalesOrder"
                || reason.hold_rule_group.is_approvable
        },
        thumbsUpDisabled(reason) {
            if (this.thumbsDisabled) {
                return true;
            }

            if (!this.isReasonApprovable(reason)) {
                return true;
            }

            return false;
        },
    },
}
</script>

<style scoped lang="scss">
.invisible {
    visibility: hidden;
}

.icon-dash {
    font-size: 3rem;
    line-height: 50%;
    cursor: default
}

button:disabled {
    cursor: not-allowed;
}

.card-header {
    font-weight: initial;
    font-weight: normal;
}

.hold-reasons-list {
    .btn-group-sm {
        display: flex;
        gap: .5rem;
        flex-wrap: wrap;

        .btn {
            height: 2rem;
            width: 2rem;
            padding: 1rem;
            font-size: 1.25rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
    }
}
</style>
